import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SelfReport } from './SelfReport';
import { displayModelName } from '../../../helpers/modelName';

/**
 * How every model answered the same question.
 *
 * This replaces the profit-and-loss panel on member research. That panel
 * measured a trading rule derived from the forecast — rolling return, win rate,
 * drawdown — which is the wrong frame twice over: it reads as financial
 * performance on a page that is a research notebook, and it answers a question
 * nobody asked. A member asks several models one question at once; what they
 * want to know is how the answers differed.
 *
 * Ranges are shown as percentages from the baseline, not as prices. Every row
 * shares that baseline, so the percentage is what makes them comparable at a
 * glance — "-4% to +8%" reads as a claim, "$67.93 – $76.10" reads as a number
 * you have to do arithmetic on. The prices are one click away on each row.
 *
 * System forecasts keep the old panel. They are generated per model on a
 * schedule with no shared prompt, so there is no group to compare.
 */
const PromptGroupTable = ({
  group, currentId, baseline, actualPrice, actualLow, actualHigh,
  // When the prompt was generated, which is the moment every answer in the
  // group is dated from. Read against each model's own claimed timestamp.
  askedAt = null,
  // Renders the heading and shared facts instead of the table, so the two can
  // sit in different columns.
  summaryOnly = false,
}) => {
  const navigate = useNavigate();

  // A group of one is still worth showing. Fifteen member forecasts predate
  // prompt storage and have no siblings to compare against, and refusing to
  // render took the shared facts and the forecast's own score down with the
  // comparison -- a fifth of member research showed an empty panel.
  if (!Array.isArray(group) || group.length === 0) return null;
  const alone = group.length === 1;

  // Number(null) is 0 and Number('') is 0, so a missing value has to be rejected
  // before the conversion -- otherwise an unscored row reports a score of zero,
  // and a missing price renders as $0.00.
  const num = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const money = (v) => (num(v) === null ? '—' : `$${num(v).toFixed(2)}`);

  const base = num(baseline);
  const actual = num(actualPrice);

  const asPct = (v) => {
    const n = num(v);
    if (n === null || !base) return null;
    return ((n - base) / base) * 100;
  };
  const showPct = (v) => {
    const n = num(v);
    return n === null ? '—' : `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
  };

  /**
   * A forecast range as a move, measured from whatever the model was working
   * from.
   *
   * Its own reported price first, and the settled baseline only as a fallback.
   * A range is a claim about a move, and the move a model was claiming is
   * relative to where it thought price was -- reading its range against a
   * baseline it never saw describes something it did not say. It also means the
   * column is populated from the moment the answer is pasted, rather than
   * waiting for a settlement that may be a month away.
   *
   * The consequence is deliberate and is what the next column is for: a model
   * that mispriced the asset can show a sensible-looking move here and still
   * score badly, because the score is measured against the real price. "Price
   * it found" is what reconciles the two.
   *
   * Dollars when there is no denominator at all -- an older answer that
   * reported no price, before settlement.
   */
  const bandFrom = (lo, hi, from) => {
    const base0 = num(from);
    const a = num(lo);
    const b = num(hi);
    if (a === null || b === null) return null;
    if (!base0) return `${money(Math.min(a, b))} – ${money(Math.max(a, b))}`;
    const pcts = [a, b].map((v) => ((v - base0) / base0) * 100).sort((x, y) => x - y);
    return `${showPct(pcts[0])} to ${showPct(pcts[1])}`;
  };

  // Both ends of a band, as percentages from the baseline.
  const bandPct = (lo, hi) => {
    const a = asPct(lo);
    const b = asPct(hi);
    if (a === null || b === null) return null;
    const [low, high] = a <= b ? [a, b] : [b, a];
    return `${showPct(low)} to ${showPct(high)}`;
  };

  // Derived by the API from the range, the baseline and the close, so every
  // member forecast is scored the same way regardless of when or how it was
  // settled. Null means the forecast made no directional call, which is an
  // abstention rather than a zero.
  const scoreOf = (r) => num(r.member_score);

  /**
   * Which way a forecast points, beside its range.
   *
   * The direction comes from the API, not from comparing the midpoint to the
   * baseline here. Those two disagree on most rows: a range only counts as
   * directional when the baseline sits far enough from its centre, and 55% of
   * member research fails that test while still having a midpoint on one side.
   * Reading the midpoint would print a confident green arrow next to a score
   * the scoring page explains as "no direction was called".
   *
   * So a straddling range gets its own grey mark rather than being forced into
   * up or down. It is the most common case, and it is a real answer.
   */
  const DirectionArrow = ({ direction }) => {
    const marks = {
      up: { glyph: '▲', color: '#0ab39c', title: 'Forecast points up' },
      down: { glyph: '▼', color: '#f06548', title: 'Forecast points down' },
      none: {
        // A dot, not a dash. Every range in this column can start with a minus
        // sign, and a grey dash in front of "-3.0%" reads as part of the
        // number rather than as a mark of its own.
        glyph: '●', color: '#adb5bd',
        title: 'The range straddles the price when asked: no direction called',
      },
    };
    const mark = marks[direction];
    if (!mark) return null;
    return (
      <span
        title={mark.title}
        aria-label={mark.title}
        style={{ color: mark.color, marginRight: 6, fontSize: '0.85em' }}
      >
        {mark.glyph}
      </span>
    );
  };

  /**
   * What a model said the asset cost, and how far that was from the truth.
   *
   * Two numbers doing different jobs. The price is the claim; the error is what
   * it cost, and it only exists once a settlement has established the real
   * figure. Under a percent is shown in grey rather than green: it is not an
   * achievement, it is the absence of a problem, and colouring it would put a
   * row of ticks beside every ordinary answer.
   */
  const PriceFound = ({ reported }) => {
    const found = num(reported);
    if (found === null) {
      return <span className="text-muted" title="This answer did not report a price">—</span>;
    }
    const err = base ? Math.abs(found - base) / base : null;
    const heavy = err !== null && err > 0.01;
    return (
      <>
        <div style={{ fontFamily: 'monospace' }}>{money(found)}</div>
        {err !== null && (
          <div className={`fs-12 ${heavy ? 'text-danger' : 'text-muted'}`}>
            {err < 0.0001 ? 'exact' : `${(err * 100).toFixed(err < 0.1 ? 1 : 0)}% off`}
          </div>
        )}
      </>
    );
  };

  const Shared = ({ label, value, sub }) => (
    <div className="me-4 mb-2">
      <div className="text-muted fs-12">{label}</div>
      <div className="fw-bold fs-14">{value}</div>
      {sub && <div className="text-muted fs-12">{sub}</div>}
    </div>
  );

  const actualBand = bandPct(actualLow, actualHigh);

  // The row being viewed. Its bounds are the one figure in the strip below that
  // is NOT shared with the rest of the group — every model answered with its
  // own range — so it is labelled for the model rather than left to look like a
  // property of the question.
  const current = group.find((r) => Number(r.id) === Number(currentId));

  // The heading and the facts the whole group shares. Rendered in the other
  // column, above the prompt: they describe the question, and the prompt is the
  // question, so they belong together. What is left beside them is the table,
  // which is the answers.
  if (summaryOnly) {
    return (
      <div className="mb-4">
        <h5 className="text-dark mb-1">{alone ? 'This forecast' : 'How each model answered'}</h5>
        <div className="text-muted fs-13 mb-3">
          {alone
            ? 'No price was given — finding it was part of the question. The range is shown'
              + ' as a move from the price the model itself found.'
            : `Same question, same window — asked of ${group.length} models at once.`
              + ' No price was given: finding it was part of the question, so each range is'
              + ' shown as a move from the price that model found.'}
        </div>

        {/* Stated once. Repeating the shared ones per row would suggest they
            varied between models, and they cannot: the group shares a prompt. */}
        <div className="d-flex flex-wrap pb-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
          {/* Entered at settlement, not when the forecast was made. Saying
              "set at settlement" rather than showing a dash distinguishes a
              figure that is still to come from one that went missing. */}
          <Shared
            label="Price when asked"
            value={base ? money(base) : 'Set at settlement'}
            sub={base ? null : 'Each model reported its own — see the table'}
          />
          {/* In dollars here, as percentages in the table. Both are wanted for
              different reasons: the percentage is what makes one model's answer
              comparable to another's, and the price is what the reader is
              actually watching. Only the table can carry the percentage for
              every model, and only this line has room for the price. */}
          {current && num(current.range_min) !== null && num(current.range_max) !== null && (
            <Shared
              // Parenthesised, not dash-separated: some model names carry an em
              // dash of their own, and a second one made the line read as three
              // things instead of two. The name is the model's own, matching
              // the column below rather than naming the catalogue slot.
              label={alone ? 'Forecast range' : `Forecast range (${displayModelName(current.model_label, current.model_id, current.model_self_report)})`}
              value={`${money(current.range_min)} – ${money(current.range_max)}`}
              // Measured from the same number the table uses for this row, so
              // the strip and the table cannot describe one range two ways.
              sub={(() => {
                const from = num(current.model_reported_price) ?? base;
                return from ? bandFrom(current.range_min, current.range_max, from) : null;
              })()}
            />
          )}
          {actual !== null && (
            <Shared label="Actual close" value={money(actual)} sub={showPct(asPct(actual))} />
          )}
          {num(actualLow) !== null && num(actualHigh) !== null && (
            <Shared
              label="Actual range"
              value={`${money(actualLow)} – ${money(actualHigh)}`}
              sub={actualBand}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table className="table table-sm align-middle mb-0 fs-13">
          <thead>
            <tr className="text-muted fs-12">
              <th>Model</th>
              <th className="text-end">Forecast range</th>
              {/* What each model believed the asset cost. The one column here
                  that is about the model rather than about its forecast: a
                  range built from a price the asset was never near says
                  nothing, however well it reads. */}
              <th className="text-end">Price it found</th>
              <th className="text-end">Confidence</th>
              <th className="text-end">Score</th>
            </tr>
          </thead>
          <tbody>
            {group.map((r) => {
              const isCurrent = Number(r.id) === Number(currentId);
              // Its own price when it reported one, the settled baseline
              // otherwise -- and the arrow measured from whichever was used.
              const from = num(r.model_reported_price) ?? base;
              const band = bandFrom(r.range_min, r.range_max, from);
              // Preferred, then fallen back on, rather than chosen by whether
              // a price was reported. An API that has not been redeployed sends
              // no member_direction_self at all, and keying off the price meant
              // reading undefined and drawing nothing -- every arrow in the
              // column disappeared, including the ones the old field could
              // still have supplied.
              const arrow = r.member_direction_self || r.member_direction || null;
              const score = scoreOf(r);
              return (
                <tr
                  key={r.id}
                  onClick={() => { if (!isCurrent) navigate(`/prediction/${r.id}`); }}
                  // The row is a way through to that model's prompt and its full
                  // answer, which is where the reasoning behind a range lives.
                  style={{
                    cursor: isCurrent ? 'default' : 'pointer',
                    background: isCurrent ? '#eef4ff' : undefined,
                    fontWeight: isCurrent ? 600 : undefined,
                  }}
                  title={isCurrent ? 'You are reading this one' : 'Open this forecast'}
                >
                  <td>
                    {/* The version the model gave for itself, not the catalogue
                        family it was filed under: "GPT-5.6 Luna" rather than
                        "ChatGPT — GPT-5", and a real name rather than "Other
                        model" for anything off the list. Which model wrote a
                        forecast is the column's whole job, and the stored label
                        is now derived from this same self-report anyway. */}
                    {displayModelName(r.model_label, r.model_id, r.model_self_report)}
                    {isCurrent && (
                      <span className="badge bg-primary ms-2 fw-normal fs-12">
                        viewing
                      </span>
                    )}
                    {/* Only speaks up when the model dated its own answer far
                        from when it was filed -- it was not reading a clock. */}
                    <SelfReport
                      label={r.model_label}
                      modelId={r.model_id}
                      claimed={r.model_self_report}
                      answeredAt={r.model_answered_at}
                      askedAt={askedAt}
                    />
                  </td>
                  <td className="text-end" style={{ fontFamily: 'monospace' }}>
                    <DirectionArrow direction={arrow} />
                    {band || '—'}
                  </td>
                  <td className="text-end">
                    <PriceFound reported={r.model_reported_price} />
                  </td>
                  <td className="text-end">
                    {num(r.confidence) === null ? '—' : `${num(r.confidence).toFixed(0)}%`}
                  </td>
                  <td className="text-end">
                    {!r.is_scored ? (
                      <span className="text-muted">Not settled</span>
                    ) : score === null ? (
                      // Settled, but something the calculation needs is absent.
                      <span className="text-muted" title="Not enough recorded to score this">N/A</span>
                    ) : (
                      // The number alone. What is behind it is on the linked
                      // page rather than repeated under every row -- and a
                      // label that has to guess when its field is missing gets
                      // the guess wrong, which is how a column of correct calls
                      // came to be captioned "direction wrong".
                      <span
                        className={score >= 60 ? 'text-success fw-bold' : 'text-danger'}
                        title={
                          r.member_verdict === 'right' ? 'Direction called correctly'
                            : r.member_verdict === 'wrong' ? 'Direction called wrongly'
                              : r.member_verdict === 'no-call' ? 'The range straddles the baseline: no direction called'
                                : undefined
                        }
                      >
                        {score.toFixed(1)}
                      </span>
                    )}
                    {/* What the forecast would have scored had the model priced
                        the asset correctly. Shown only when the discount
                        actually bit: without it a sound-looking range scoring
                        0.6 reads as a bug rather than as the consequence it
                        is. */}
                    {num(r.member_score_before_price_trust) !== null && (
                      <div className="text-muted fs-12" title="Discounted because the model mispriced the asset">
                        was {num(r.member_score_before_price_trust).toFixed(1)}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PromptGroupTable;
